import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';

import { EventModel, EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { Nullable, StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { orderBy } from 'lodash-es';

import { SourceOfPick } from '../betslip-base/models/pick-models';
import { BetBuilderOptionComponent } from './bet-builder-option.component';
import { CorrectScoreOptions, CorrectScoreService } from './correct-score.service';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionGroupService } from './option-group.service';
import { OptionPanelBottomActionComponent, OptionPanelBottomActionState, ShowMoreData } from './option-panel-bottom-action.component';
import { SHEET_VIEW_OPEN } from './sheet.token';

@Component({
    selector: 'ms-bet-builder-correct-score-option-group',
    templateUrl: './bet-builder-correct-score-option-group.component.html',
    standalone: true,
    imports: [NgClass, NgTemplateOutlet, MarketHelpComponent, OptionPanelBottomActionComponent, BetBuilderOptionComponent],
})
export class BetBuilderCorrectScoreOptionGroupComponent implements OnChanges, OnInit {
    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() optionGroup: EventOptionGroup;
    @Input() isShowMoreDisabled: boolean;
    @Input() showMarketHelp: boolean;
    @Input() crmPromotedOptionIdsMap: StringDictionary<number> = {};
    @Input() eventHelpText?: string;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() optionsLimitOverride?: number;

    @Output() showAllRequested = new EventEmitter<void>();

    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));
    optionsLayout: CorrectScoreOptions;
    isCollapsed = true;
    headerName: string[];
    displayMultiSelectionText?: boolean = false;
    multiSelectionHelpText: string;
    showMoreData: ShowMoreData;

    private showMoreRowsThreshold: number;
    private scoreOptionsThreshold: number;
    private additionalOptionsThreshold: number;

    constructor(
        public sitecore: Sitecore,
        private changeDetectorRef: ChangeDetectorRef,
        private correctScoreService: CorrectScoreService,
        private trackingService: TrackingService,
        private optionGroupService: OptionGroupService,
        private marketGroupingConfig: MarketGroupingConfig,
    ) {
        this.showMoreRowsThreshold = this.marketGroupingConfig.showMoreBetBuilderCorrectScoreThreshold;
    }

    ngOnInit(): void {
        if (this.sitecore.betBuilderEntainUI.messages) {
            this.multiSelectionHelpText = this.sitecore.betBuilderEntainUI.messages.MultiSelectionInfo;
        }

        this.displayMultiSelectionText = this.optionGroup.isMultipleSelection;
    }

    ngOnChanges(): void {
        let optionGroups = this.optionGroupService.getOptionGroups(this.optionGroup, (option) => option.marketAttribute);
        optionGroups = orderBy(optionGroups, (o) => o.options[0].order);

        this.optionsLayout = this.correctScoreService.mapColumnOptions(
            optionGroups[0]?.options || [],
            optionGroups[1]?.options || [],
            optionGroups[2]?.options || [],
            [],
            false,
        );

        this.headerName = optionGroups.map((opr) => opr.attribute);

        const scoreRowsCount = this.optionsLayout.options.length / this.optionsLayout.layout;
        this.scoreOptionsThreshold = this.showMoreRowsThreshold * this.optionsLayout.layout;
        this.additionalOptionsThreshold = this.showMoreRowsThreshold - scoreRowsCount;
        this.showMoreData = {
            totalCount: scoreRowsCount,
            initialVisibleCount: this.optionsLimitOverride ?? this.showMoreRowsThreshold,
            pageSize: scoreRowsCount,
        };
    }

    onShowMore({ state }: { state: OptionPanelBottomActionState; visibleCount: number }): void {
        this.isCollapsed = state === 'collapsed';
        this.changeDetectorRef.detectChanges();

        this.trackingService.track(trackingConstants.EVENT_CLICKS, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Competition Page Interactions',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'Visible Selections',
            [trackingConstants.COMPONENT_ACTION_EVENT]: this.isCollapsed ? 'Show More' : 'Show Less',
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
        });
    }

    trackOption(index: number, option: Nullable<EventOption>): string {
        return option ? `${option.id}` : `_${index}`;
    }

    isScoreOptionVisible(index: number): boolean {
        return this.isInSheetView || !this.isCollapsed || index < this.scoreOptionsThreshold;
    }

    isAdditionalOptionVisible(index: number): boolean {
        return this.isInSheetView || !this.isCollapsed || index < this.additionalOptionsThreshold;
    }

    isPlaceholderShadowVisible(index: number): boolean {
        return index >= this.optionsLayout.options.length - 2;
    }

    get scoreOptionsClass(): string {
        return this.optionsLayout.layout === 2 ? 'double' : 'triple';
    }
}
